import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { Floor } from "shared/types/Floor"
import { isProjectFloor } from "shared/types/Project"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Combobox } from "shared/ui/input/combobox/Combobox"

import { FloorTitle } from "../floor-title/FloorTitle"
import "./styles/_floor-input.scss"

interface ParentOptionValueProps {
  value: SelectOption<string | null, Floor> | null
}

const ParentOptionValue = React.memo(({ value }: ParentOptionValueProps) => {
  const { t } = useTranslation()

  if (!value?.data) {
    return value ? (
      <span>{value.label}</span>
    ) : (
      <span className="combobox__placeholder">{t("General.Select")}</span>
    )
  }

  if (isProjectFloor(value.data)) {
    return <FloorTitle floor={{ label: value.data.label }} />
  }

  return <span>{value.label}</span>
})

ParentOptionValue.displayName = "ParentOptionValue"

export interface FloorInputProps extends WithClassName {
  options: SelectOption<string | null, Floor>[] | undefined
  name?: string
  readonly?: boolean
  disabled?: boolean
  loading?: boolean
}

export const FloorInput = React.memo(
  ({
    options,
    readonly,
    loading,
    disabled,
    name,
    className
  }: FloorInputProps) => {
    const { t } = useTranslation()

    const renderValue = useCallback(
      (value: SelectOption<string | null, Floor> | null) => (
        <ParentOptionValue value={value} />
      ),
      []
    )

    return (
      <Combobox.Root
        name={name}
        label={t("Floor.Floor")}
        inputID="floor-input"
        className={classNames("floor-input", className)}
        readonly={readonly}
        loading={loading}
        disabled={disabled}
        items={options}
      >
        <Combobox.Label />
        {!readonly && (
          <Combobox.Control>
            <Combobox.Trigger>
              <Combobox.Value>{renderValue}</Combobox.Value>
              <Combobox.Icon />
            </Combobox.Trigger>
            <Combobox.Portal>
              <Combobox.Positioner
                className="floor-input__positioner"
                align="start"
                sideOffset={8}
              >
                <Combobox.Popup aria-label={t("Floor.Floor")}>
                  <Combobox.InputContainer>
                    <Combobox.Input />
                    <Icon name={IconName.SEARCH} />
                  </Combobox.InputContainer>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(option: SelectOption<string | null, Floor>) => (
                      <Combobox.Item
                        key={option.value ?? "null"}
                        value={option}
                      >
                        <Combobox.ItemIndicator />
                        <Combobox.ItemContent>
                          <ParentOptionValue value={option} />
                        </Combobox.ItemContent>
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Control>
        )}
        <Combobox.Readonly items={options} />
        <Combobox.ErrorMessage />
      </Combobox.Root>
    )
  }
)
